'use client';

import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import { Card, CardContent, CardFooter } from '@ahafood/ui/components/card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { ArrowRight, Clock } from 'lucide-react';
import React, { useEffect, useState, type PropsWithChildren } from 'react';
import type { Store } from '@/api/ahafood/get-store-fn';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { Link, useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile/use-is-mobile';
import { useAuthStore } from '@/store';

type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

interface StoreCardProps {
  store: Store;
  index: number;
}

interface NewStoreSessionProps {
  stores: Store[];
  citySlug: string;
}

export const StoreHours: React.FC<{ openHours: OpenHoursType }> = ({
  openHours,
}) => {
  const getCurrentDayHours = (openHours: OpenHoursType): string => {
    const today = new Date().getDay();
    const dayIndex = today === 0 ? 6 : today - 1;
    const hours = openHours[dayIndex.toString()];

    if (!hours || hours.length === 0) {
      return 'Đóng cửa';
    }

    const formatTime = (minutes: number): string => {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    return hours
      .map(
        (range) =>
          `${formatTime(range.start_time)} - ${formatTime(range.end_time)}`
      )
      .join(', ');
  };
  const [currentDayHours, setCurrentDayHours] = useState<string>('');

  useEffect(() => {
    const updateHours = () => {
      setCurrentDayHours(getCurrentDayHours(openHours));
    };

    updateHours();
    const timer = setInterval(updateHours, 600000);

    return () => clearInterval(timer);
  }, [openHours]);

  return (
    <div className="flex flex-row text-sm text-neutral-60">
      <Clock className="mr-1 size-4 self-center" />
      <span className="items-center truncate">{currentDayHours}</span>
    </div>
  );
};

export default function NewStoreSession({
  stores,
  citySlug,
}: NewStoreSessionProps) {
  const isMobile = useIsMobile();
  const router = useRouter();

  const handleDealToggle = () => {
    router.push(`/${citySlug}/danh-sach-cua-hang-moi`);
  };

  if (!stores || stores.length === 0) return null;

  return (
    <StoreListWrapper citySlug={citySlug}>
      <FreeModeSwiper
        navigation={false}
        className="w-full !overflow-visible"
        spaceBetween={isMobile ? 16 : 24}
        pagination
      >
        {stores.map((store, index) => (
          <FreeModeSwiper.Slide
            key={store._id}
            style={{ width: isMobile ? '45%' : 256 }}
          >
            <NewStoreItem store={store} index={index} />
          </FreeModeSwiper.Slide>
        ))}
        {stores.length > 5 && (
          <FreeModeSwiper.Slide style={{ width: isMobile ? '45%' : 256 }}>
            <Card
              className="flex h-[247px] flex-col items-center justify-center border-none bg-neutral-10 md:h-[332px]"
              onClick={handleDealToggle}
            >
              <CardContent className="flex h-full flex-col items-center justify-center gap-4">
                <div className="rounded-full bg-neutral-15 p-4 md:p-10">
                  <ArrowRight className="size-20 text-neutral-70" />
                </div>
              </CardContent>
              <CardFooter className="text-xl font-bold text-neutral-70">
                Xem thêm
              </CardFooter>
            </Card>
          </FreeModeSwiper.Slide>
        )}
      </FreeModeSwiper>
    </StoreListWrapper>
  );
}

export function NewStoreItem({ store }: StoreCardProps) {
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuthStore();

  const handleStoreClick = () => {
    trackEvent('click_button', {
      button_name: 'store_detail',
      button_location: 'new_store_section',
      storeId: store._id,
      storeSlug: store.slug,
      storeName: store.name,
      user_id: user?.cunstomerId,
    });
    router.push(routes.storeDetail(store.slug));
  };

  const imageSrc =
    store.image_url || `${assetPrefix}/static/images/empty-store.webp`;

  return (
    <Button
      onClick={handleStoreClick}
      variant="ghost"
      className="group relative flex h-auto w-full flex-col justify-start overflow-hidden rounded-xl bg-white p-0 shadow-[4px_4px_10px_rgba(0,0,0,0.1),-4px_-4px_10px_rgba(0,0,0,0.1)] transition-all hover:shadow-[8px_8px_16px_rgba(0,0,0,0.1),-8px_-8px_16px_rgba(0,0,0,0.1)] md:w-64"
    >
      <div className="relative aspect-square h-auto w-full bg-white">
        <Avatar className="size-full rounded-none bg-neutral-10">
          <AvatarImage
            src={imageSrc}
            alt={store.name}
            style={{ objectFit: 'cover' }}
            className="size-full"
          />
          <AvatarFallback className="size-full">
            {store.name ? store.name.charAt(0) : 'S'}
          </AvatarFallback>
        </Avatar>
      </div>

      <div className="w-full space-y-1 px-4 py-3 font-medium capitalize text-neutral-50">
        <h3 className="w-full truncate text-left text-sm font-bold text-neutral-70 md:text-xl">
          {store.name}
        </h3>
        <StoreHours openHours={store.open_hours} />
      </div>
    </Button>
  );
}

export function StoreListWrapper({
  children,
  citySlug,
}: PropsWithChildren<{ citySlug: string }>) {
  return (
    <section className="ahf-deal bg-white">
      <div
        className={cn(
          'md:p relative z-[1] mx-auto max-w-screen-xl gap-y-6 px-4 pb-6 pt-10 md:px-6 md:pt-20'
        )}
      >
        <h2 className="relative z-[5] mb-6 flex justify-between space-x-2 text-2xl font-bold drop-shadow-sm md:text-5xl items-center">
          <div>
            <span>🔥</span> <span>Quán mới</span>
          </div>
          <Link
            href={`/${citySlug}/danh-sach-cua-hang-moi`}
            className="inline-flex items-center gap-1 text-lg font-bold text-neutral-70 hover:text-neutral-80 transition-colors"
          >
            <span className="hidden md:inline-block">Xem thêm</span>
            <ArrowRight className="size-6" />
          </Link>
        </h2>
        {children}
      </div>
    </section>
  );
}
